import { Controller } from "@hotwired/stimulus"

// Scene-aware text-path editor — theme-agnostic control-point editor for a
// PathSpec (see shared/types/textPath.ts). Two modes:
//   • parametric — pick a curve family (a <select>, submits [path][family]).
//   • points     — drag control points on a 9:16 mini-frame; the handles write
//                  [path][points][][x]/[y] hidden inputs (normalised 0..1).
// It only WRITES hidden form fields + dispatches bubbling input events; the
// host's live-preview controller (which re-POSTs the whole form) re-renders the
// real Remotion bundle, so the editor never approximates the path itself.
//
// Reusable across templates: the host passes the field prefix + an accent CSS
// var; nothing here is music-promo-specific.
export default class extends Controller {
  static targets = ["kind", "parametric", "pointsPane", "canvas", "pointsContainer", "handles", "pathLine", "suggestions"]
  static values = { prefix: String, points: Array, suggestions: Array }

  connect() {
    const seed = this.hasPointsValue && this.pointsValue.length ? this.pointsValue : this.defaultPoints()
    this.points = seed.map((p) => ({ x: clamp01(Number(p.x)), y: clamp01(Number(p.y)) }))
    this.dragIndex = null
    this.onMove = this.pointerMove.bind(this)
    this.onUp = this.pointerUp.bind(this)
    this.syncKindUI()
    this.render()
    this.syncInputs(false)
  }

  disconnect() {
    document.removeEventListener("pointermove", this.onMove)
    document.removeEventListener("pointerup", this.onUp)
  }

  defaultPoints() {
    return [{ x: 0.15, y: 0.5 }, { x: 0.5, y: 0.38 }, { x: 0.85, y: 0.5 }]
  }

  // ---- kind toggle ----------------------------------------------------------

  kindChanged() {
    this.syncKindUI()
    this.notify()
  }

  syncKindUI() {
    const kind = this.hasKindTarget ? this.kindTarget.value : "parametric"
    if (this.hasParametricTarget) this.parametricTarget.hidden = kind !== "parametric"
    if (this.hasPointsPaneTarget) this.pointsPaneTarget.hidden = kind !== "points"
  }

  // ---- point editing --------------------------------------------------------

  canvasPointerDown(event) {
    event.preventDefault()
    const p = this.toFraction(event)
    const near = this.nearestPoint(p)
    if (near != null) {
      this.dragIndex = near
    } else {
      this.points.push(p)
      this.dragIndex = this.points.length - 1
    }
    document.addEventListener("pointermove", this.onMove)
    document.addEventListener("pointerup", this.onUp)
    this.render()
    this.syncInputs()
  }

  pointerMove(event) {
    if (this.dragIndex == null) return
    this.points[this.dragIndex] = this.toFraction(event)
    this.render()
    this.syncInputs()
  }

  pointerUp() {
    this.dragIndex = null
    document.removeEventListener("pointermove", this.onMove)
    document.removeEventListener("pointerup", this.onUp)
  }

  // Remove a point (min 2) — wired to a handle's contextmenu / a remove button.
  removePoint(event) {
    event.preventDefault()
    const i = Number(event.params?.index ?? event.currentTarget?.dataset?.index)
    if (Number.isInteger(i) && this.points.length > 2) {
      this.points.splice(i, 1)
      this.render()
      this.syncInputs()
    }
  }

  reset() {
    this.points = this.defaultPoints()
    this.render()
    this.syncInputs()
  }

  // ---- scene-aware suggestions ----------------------------------------------

  // Apply a VLM-suggested placement: load its points, switch to Custom (points)
  // mode, and re-render so the user can immediately drag to fine-tune.
  applySuggestion(event) {
    event.preventDefault()
    const i = Number(event.params?.index ?? event.currentTarget?.dataset?.index)
    const suggestion = (this.hasSuggestionsValue ? this.suggestionsValue : [])[i]
    if (!suggestion || !Array.isArray(suggestion.points) || suggestion.points.length < 2) return

    this.points = suggestion.points.map((p) => ({ x: clamp01(Number(p.x)), y: clamp01(Number(p.y)) }))
    if (this.hasKindTarget) this.kindTarget.value = "points"
    this.syncKindUI()
    this.render()
    this.syncInputs()
  }

  // ---- helpers --------------------------------------------------------------

  toFraction(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    const w = rect.width || 1
    const h = rect.height || 1
    return { x: clamp01((event.clientX - rect.left) / w), y: clamp01((event.clientY - rect.top) / h) }
  }

  nearestPoint(p) {
    const THRESH = 0.06
    let best = null
    let bestD = THRESH
    this.points.forEach((q, i) => {
      const d = Math.hypot(q.x - p.x, q.y - p.y)
      if (d < bestD) { bestD = d; best = i }
    })
    return best
  }

  // Redraw the handles + the SMOOTHED curve inside the canvas SVG, so the editor
  // previews the same Catmull-Rom spline the renderer draws (not the raw polygon).
  // Visual aid only — the authoritative render is the live preview iframe.
  render() {
    if (!this.hasHandlesTarget) return
    const W = 90
    const H = 160
    const curve = this.smoothedPoints()
    if (this.hasPathLineTarget) {
      this.pathLineTarget.setAttribute("points", curve.map((p) => `${(p.x * W).toFixed(1)},${(p.y * H).toFixed(1)}`).join(" "))
    }
    this.handlesTarget.innerHTML = this.points
      .map((p, i) => `<circle cx="${(p.x * W).toFixed(1)}" cy="${(p.y * H).toFixed(1)}" r="3.4" class="tpe-handle" data-index="${i}"></circle>`)
      .join("")
  }

  // Uniform Catmull-Rom sampling in fraction space — mirrors the catmullRom in
  // shared/text/paths.ts so the editor preview matches the rendered spline.
  smoothedPoints() {
    const P = this.points
    if (P.length < 3) return P
    const at = (i) => P[Math.max(0, Math.min(P.length - 1, i))]
    const out = [P[0]]
    const STEPS = 14
    for (let i = 0; i < P.length - 1; i += 1) {
      const p0 = at(i - 1)
      const p1 = at(i)
      const p2 = at(i + 1)
      const p3 = at(i + 2)
      const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 }
      const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 }
      for (let s = 1; s <= STEPS; s += 1) {
        const t = s / STEPS
        const mt = 1 - t
        out.push({
          x: mt * mt * mt * p1.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * p2.x,
          y: mt * mt * mt * p1.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * p2.y,
        })
      }
    }
    return out
  }

  // Rebuild the hidden [path][points][][x]/[y] inputs (paired, in order) so Rails
  // parses an ordered array of {x,y}. dispatch=true fires the preview refresh.
  syncInputs(dispatch = true) {
    if (!this.hasPointsContainerTarget) return
    const prefix = this.prefixValue
    this.pointsContainerTarget.innerHTML = this.points
      .map(
        (p) =>
          `<input type="hidden" name="${prefix}[points][][x]" value="${p.x.toFixed(4)}">` +
          `<input type="hidden" name="${prefix}[points][][y]" value="${p.y.toFixed(4)}">`,
      )
      .join("")
    if (dispatch) this.notify()
  }

  notify() {
    // Fire from a NAMED field inside the editor (the kind <select> or a hidden
    // point input) so the host's live-preview controller — which filters on
    // event.target.name starting with the config prefix — actually refreshes.
    // An event dispatched on the bare editor <div> has no name and is ignored,
    // which is why dragging points / picking a suggestion didn't update the
    // preview until the user toggled another named field.
    const named = this.element.querySelector("input[name], select[name]")
    ;(named || this.element).dispatchEvent(new Event("input", { bubbles: true }))
  }
}

function clamp01(v) {
  if (!Number.isFinite(v)) return 0
  return Math.max(0, Math.min(1, v))
}
